import {
  Alert,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  IconAddLine,
  IconArrowStartLine,
  IconExternalLinkLine,
  List,
  Spinner,
  Text,
  ToggleDetails,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate } from "react-router-dom";
import { AssessmentPlanService } from "../../../services/AssessmentService";
import { EmbeddedDashboardService } from "../../../services/EmbeddedDashboardService";
import { Files } from "../../../services/FilesService";
import { QualitativeMeasureService } from "../../../services/QualitativeMeasureService";
import { ShareCodeService } from "../../../services/ShareCodeService";
import { fileWriter } from "../../../utils/FileUtils";
import { ApDataContext } from "../../../views/Plan";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import CustomRTE, { EMPTY_RTE } from "../../CustomTextArea/CustomRTE";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import { CycleSelect } from "../../Selectors/CyclesSelector";
import ResultsPreview from "./ResultsPreview/ResultsPreview";
import ReviewersCard from "./Reviewers/ReviewersCard";
import PlanSectionCard from "./Sections/PlanSectionCard";
import TemplateSectionCard from "./Sections/TemplateSectionCard";
import {
  CANVAS,
  EMBEDDED,
  EXTERNAL,
  QUALITATIVE,
  emailVerification,
} from "./utils";

const ManagePlan = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showNotification = useNotification();
  const [loadingPage, setLoadingPage] = useState(!!location.state.plan);
  const [selectedCycle, setSelectedCycle] = useState(
    location.state.plan
      ? location.state.cycles.find(
          (cycle) => cycle.id === location.state.plan.cycle.id
        )
      : {}
  );
  const [mission, setMission] = useState(EMPTY_RTE);
  const [approver, setApprover] = useState(
    location.state.plan?.results_purl?.approvers ?? ""
  );
  const [reviewers, setReviewers] = useState([]);
  const [sections, setSections] = useState([]);

  const [runningIndex, setRunningIndex] = useState(0);
  const [enableCycleSelect, setEnableCycleSelect] = useState(true);
  const [confirmBackDialog, setConfirmBackDialog] = useState(false);
  const [upsertingPlan, setUpsertingPlan] = useState(false);

  const [includeMission, setIncludeMission] = useState(false);
  const [includeGoalsObjs, setIncludeGoalsObjs] = useState(
    location.state.plan?.include_goals_objs
  );
  const [resendingResults, setResendingResults] = useState(false);
  const inPlan = !!location.state.plan;

  useEffect(() => {
    const loadMissionAndSections = async () => {
      if (inPlan) {
        try {
          const missionRes = await Files.getFileContent(
            "response.html",
            "mission",
            location.state.plan.id
          );
          if (missionRes.content) {
            setMission(missionRes.content);
            setIncludeMission(true);
          }
          const planSections = location.state.plan.sections.sort(
            (a, b) => a.sequence - b.sequence
          );
          for (const section of planSections) {
            section.assessments.forEach((measure) => {
              if (measure.assessment_type === EXTERNAL) {
                measure.type = EXTERNAL;
              } else {
                measure.type = CANVAS;
              }
            });
            for (const measure of section.qualitative_measures) {
              measure.type = QUALITATIVE;
              const file = await Files.getFileContent(
                "response.html",
                "qualitativeMeasure",
                measure.ddl_id
              );
              if (file.content) {
                measure.instructions = file.content;
              } else {
                measure.instructions = EMPTY_RTE;
              }
            }
            section.embedded_datalinks.forEach((measure) => {
              measure.type = EMBEDDED;
            });
            section.measures = section.assessments
              .concat(section.qualitative_measures)
              .concat(section.embedded_datalinks)
              .sort((a, b) => a.sequence - b.sequence);
            if (typeof section.reviewers === "string") {
              section.reviewers = section.reviewers.split(",");
            }
          }
          setReviewers(location.state.plan.reviewers?.split(",") ?? []);
          setSections(planSections);
          setLoadingPage(false);
        } catch {
          showNotification(
            t("notifications.general.fetchError", { obj: "Assessment Plan" }),
            ERROR
          );
        }
      }
    };
    loadMissionAndSections();
  }, [location, inPlan, showNotification]);

  const handleCycleSelect = (cycleId) => {
    setSelectedCycle(
      location.state.availCycles.find((cycle) => cycle.id === cycleId)
    );
  };

  const handleCreateMeasure = useCallback(
    (newMeasure, sectionIndex, measureIndex) => {
      setEnableCycleSelect(false);
      newMeasure.sequence = measureIndex;

      const newSections = sections.map((section, idx) => {
        if (idx === sectionIndex) {
          return {
            ...section,
            measures: section.measures.map((measure, mIdx) => {
              if (mIdx === measureIndex) {
                return newMeasure;
              }
              return measure;
            }),
          };
        }
        return section;
      });
      setSections(newSections);

      let reviewers = [];
      newSections.forEach((section) => {
        section.measures.forEach((measure) => {
          if (measure.type === QUALITATIVE && !!measure.ddl_id) {
            reviewers = reviewers.concat(measure.assignees.split(","));
            reviewers = Array.from(new Set(reviewers));
          }
        });
      });
    },
    [sections]
  );

  const deleteFromDatabase = async (measure) => {
    if (measure.type === CANVAS || measure.type === EXTERNAL) {
      await AssessmentPlanService.deleteAssessment(measure.id);
    } else if (measure.type === QUALITATIVE) {
      await QualitativeMeasureService.deleteMeasure(measure.ddl_id);
    } else if (measure.type === EMBEDDED) {
      await EmbeddedDashboardService.deleteEmbeddedDashboard(measure.ddl_id);
    }
  };

  const handleDeleteMeasure = useCallback(
    async (measure, sectionIndex) => {
      await deleteFromDatabase(measure)
        .then(() => {
          const newSections = [...sections];
          newSections[sectionIndex].measures = newSections[
            sectionIndex
          ].measures.filter(
            (m) => m.id !== measure.id || m.ddl_id !== measure.ddl_id
          );
          setSections(newSections);

          let reviewers = [];
          newSections.forEach((section) => {
            section.measures.forEach((measure) => {
              if (measure.type === QUALITATIVE && !!measure.ddl_id) {
                reviewers = reviewers.concat(measure.assignees.split(","));
                reviewers = Array.from(new Set(reviewers));
              }
            });
          });
          showNotification(
            t("notifications.general.deleteSuccess", { obj: "Measure" })
          );
        })
        .catch(() =>
          showNotification(
            t("notifications.general.deleteError", { obj: "Measure" }),
            ERROR
          )
        );
    },
    [sections, showNotification]
  );

  const handleAddSection = () => {
    setSections((prevSections) => [
      ...prevSections,
      {
        index: runningIndex,
        name: "",
        description: "",
        measures: [],
        approvers: "",
        reviewers: [],
      },
    ]);
    setRunningIndex(runningIndex + 1);
  };

  const formVerification = () => {
    if (!approver) {
      showNotification(
        t("assessment.planRequirements.approverRequired"),
        ERROR
      );
      return false;
    }
    if (!emailVerification(approver)) {
      showNotification(t("assessment.planRequirements.invalidApprover"), ERROR);
      return false;
    }
    if (reviewers.length === 0) {
      showNotification(
        t("assessment.planRequirements.reviewersRequired"),
        ERROR
      );
      return false;
    }
    if (includeMission && mission === EMPTY_RTE) {
      showNotification(t("assessment.planRequirements.missionRequired"), ERROR);
      return false;
    }
    let missingSectionApprover = false;
    let missingSectionReviewer = false;
    sections.forEach((section) => {
      if (!section.approvers) {
        missingSectionApprover = true;
      }
      if (section.reviewers.length === 0) {
        missingSectionReviewer = true;
      }
    });
    if (missingSectionApprover) {
      showNotification(
        t("assessment.planRequirements.sectionApproversRequired"),
        ERROR
      );
      return false;
    }
    if (missingSectionReviewer) {
      showNotification(
        t("assessment.planRequirements.sectionReviewersRequired"),
        ERROR
      );
      return false;
    }
    let missingMeasure = false;
    sections.forEach((section) => {
      section.measures.forEach((measure) => {
        if (!measure.id && !measure.ddl_id) {
          missingMeasure = true;
        }
      });
    });
    if (missingMeasure) {
      showNotification(
        t("assessment.planRequirements.measuresRequired"),
        ERROR
      );
      return false;
    }
    return true;
  };

  const sectionVerification = () => {
    let valid = true;
    sections.forEach((section) => {
      if (section.name === "") {
        showNotification(
          t("assessment.planRequirements.sectionHeadingRequired"),
          ERROR
        );
        valid = false;
      } else if (
        section.description === EMPTY_RTE ||
        section.description === ""
      ) {
        showNotification(
          t("assessment.planRequirements.sectionDescriptionRequired"),
          ERROR
        );
        valid = false;
      }
      section.measures.forEach((measure) => {
        if (measure.type === QUALITATIVE) {
          if (measure.name === "") {
            showNotification(
              t("assessment.planRequirements.measureNameRequired"),
              ERROR
            );
            valid = false;
          }
        }
      });
    });
    return valid;
  };

  const dedupPurlReviewers = (purlReviewers) => {
    return Array.from(new Set(purlReviewers));
  };

  const handleCreatePlan = () => {
    if (formVerification() && sectionVerification()) {
      setUpsertingPlan(true);
      const plan = {
        cycleId: selectedCycle.id,
        sections: [],
        approvers: approver,
        reviewers: reviewers.toString(),
        purlReviewers: "",
        includeGoalsObjs,
      };

      let purlReviewers = [approver, ...reviewers];
      const planSections = JSON.parse(JSON.stringify(sections));
      planSections.forEach((section, i) => {
        purlReviewers = purlReviewers.concat([
          section.approvers,
          ...section.reviewers,
        ]);
        section.sequence = i;
        section.assessmentList = [];
        section.qualitativeMeasures = [];
        section.embeddedDatalinks = [];
        section.measures.forEach((measure) => {
          if (measure.type === CANVAS || measure.type === EXTERNAL) {
            section.assessmentList.push(measure.id);
          } else if (measure.type === EMBEDDED) {
            section.embeddedDatalinks.push(measure.ddl_id);
          } else if (measure.type === QUALITATIVE) {
            section.qualitativeMeasures.push(measure.ddl_id);
            purlReviewers = purlReviewers.concat(measure.assignees.split(","));
          }
        });
        section.reviewers = section.reviewers.toString();
        delete section.measures;
        delete section.index;
      });
      plan.sections = planSections;
      plan.purlReviewers = dedupPurlReviewers(purlReviewers).toString();
      const obj = { assessmentPlan: plan };
      AssessmentPlanService.createAssessmentPlan(obj)
        .then(async (newPlan) => {
          if (includeMission) {
            await handleSavePlanMission(newPlan.id);
          }
          showNotification(
            t("notifications.general.createSuccess", { obj: "Assessment Plan" })
          );
          navigate("/plan");
        })
        .catch(() => {
          showNotification(
            t("notifications.general.createError", { obj: "Assessment Plan" }),
            ERROR
          );
        })
        .finally(() => setUpsertingPlan(false));
    }
  };

  const handleUpdatePlan = async () => {
    if (formVerification() && sectionVerification()) {
      try {
        setUpsertingPlan(true);
        let purlReviewers = [approver, ...reviewers];
        const sectionMeasureSequences = [];
        sections.forEach((section, i) => {
          if (section.id) {
            purlReviewers = purlReviewers.concat([
              section.approvers,
              ...section.reviewers,
            ]);
            sectionMeasureSequences.push({ id: section.id, sequence: i });

            section.measures.forEach((measure, j) => {
              if (measure.assignees && measure.assignees.length) {
                purlReviewers = purlReviewers.concat(
                  measure.assignees.split(",")
                );
              }

              sectionMeasureSequences.push({
                id: measure.id ?? measure.ddl_id,
                sequence: j,
              });
            });
          }
        });
        await AssessmentPlanService.updateAssessmentPlan(
          location.state.plan.id,
          {
            approvers: approver,
            reviewers: reviewers.toString(),
            purlReviewers: dedupPurlReviewers(purlReviewers).toString(),
            sequenceMap: sectionMeasureSequences,
            includeGoalsObjs: includeGoalsObjs,
          }
        );
        if (includeMission) {
          await handleSavePlanMission(location.state.plan.id);
        } else {
          await Files.deleteFile(
            "response.html",
            "mission",
            location.state.plan.id
          );
        }
        showNotification(
          t("notifications.general.updateSuccess", { obj: "Assessment Plan" })
        );
        navigate("/plan");
      } catch {
        showNotification(
          t("notifications.general.updateError", { obj: "Assessment Plan" }),
          ERROR
        );
      } finally {
        setUpsertingPlan(false);
      }
    }
  };

  const handleIncludeParameters = (values) => {
    if (values.includes("mission")) {
      setIncludeMission(true);
    } else {
      setMission(EMPTY_RTE);
      setIncludeMission(false);
    }
    if (values.includes("goalsObjs")) {
      setIncludeGoalsObjs(true);
    } else {
      setIncludeGoalsObjs(false);
    }
  };

  const handleUpdateApprover = useCallback((newApprover) => {
    setApprover(newApprover);
  }, []);

  const handleSavePlanMission = async (planId) => {
    const mission_file = fileWriter(mission, "response.html", "text/html");
    await Files.saveFile(mission_file, "mission", planId);
  };

  const handleAddReviewer = useCallback((newReviewer) => {
    setReviewers((prevReviewers) => [...prevReviewers, newReviewer]);
  }, []);

  const handleRemoveReviewer = useCallback((index) => {
    setReviewers((prevReviewers) =>
      prevReviewers.filter((_, i) => i !== index)
    );
  }, []);

  const handleSendResultsPurl = () => {
    setResendingResults(true);

    const shareData = {
      shared_path: "/results",
      external_user_ids: location.state.plan.results_purl.reviewers,
      context: {
        plan_id: location.state.plan.id,
        purl_id: location.state.plan.results_purl.ddl_id,
      },
    };
    ShareCodeService.createShareUrl(shareData)
      .then(() => {
        showNotification(t("notifications.shareCode.sendResultsSuccess"));
      })
      .catch(() => {
        showNotification(t("notifications.shareCode.sendResultsError"), ERROR);
      })
      .finally(() => setResendingResults(false));
  };

  return (
    <div style={{ margin: "-0.75rem", height: "calc(100vh - 1.5rem)" }}>
      <Flex direction="column" height="100%">
        <Flex.Item padding="small">
          <div style={{ borderBottom: "1px solid #ccc" }}>
            <Heading as="h1" level="h2">
              {inPlan ? t("assessment.managePlan") : t("assessment.createPlan")}
            </Heading>
          </div>
        </Flex.Item>
        <Flex.Item shouldGrow shouldShrink>
          <Flex height="100%" alignItems="start">
            <Flex.Item size="35%" overflowY="auto" height="100%">
              <View
                as="div"
                borderWidth="0 small 0 0"
                height="100%"
                padding="0 small 0 0"
              >
                <Flex height="100%" direction="column">
                  <Flex.Item padding="0 small small small">
                    {inPlan ? (
                      <Text>
                        <Text weight="bold">{t("cyclesAndGoals.cycle")}: </Text>
                        {location.state.plan.cycle.name}
                      </Text>
                    ) : (
                      <CycleSelect
                        selectHandler={handleCycleSelect}
                        selectedCycle={selectedCycle}
                        cycles={location.state.availCycles}
                        interaction={enableCycleSelect}
                      />
                    )}
                  </Flex.Item>
                  {Object.keys(selectedCycle).length > 0 && (
                    <Flex.Item margin="x-small 0" padding="0 small small small">
                      <View as="div" borderWidth="small 0 0 0" />
                    </Flex.Item>
                  )}
                  {Object.keys(selectedCycle).length > 0 && (
                    <Flex.Item padding="0 small medium small">
                      <CheckboxGroup
                        name="headers"
                        description={t("planTemplates.include") + ":"}
                        layout="columns"
                        value={[
                          ...(includeMission ? ["mission"] : []),
                          ...(includeGoalsObjs ? ["goalsObjs"] : []),
                        ]}
                        onChange={handleIncludeParameters}
                        disabled={location.state.plan?.from_template}
                      >
                        <Checkbox
                          label={t("assessment.mission")}
                          value="mission"
                        />
                        <Checkbox
                          label={t("dashboard.goalsAndObjectives")}
                          value="goalsObjs"
                        />
                      </CheckboxGroup>
                    </Flex.Item>
                  )}
                  {Object.keys(selectedCycle).length > 0 && (
                    <Flex.Item padding="0 small x-small small">
                      <ReviewersCard
                        plan={location.state.plan}
                        handleUpdateApprover={handleUpdateApprover}
                        handleAddReviewer={handleAddReviewer}
                        handleRemoveReviewer={handleRemoveReviewer}
                      />
                    </Flex.Item>
                  )}
                  {inPlan && (
                    <Flex.Item padding="xx-small small small small">
                      <Button
                        renderIcon={
                          resendingResults ? (
                            <Spinner
                              renderTitle={t("global.pleaseWait")}
                              size="x-small"
                            />
                          ) : (
                            IconExternalLinkLine
                          )
                        }
                        color="primary"
                        size="small"
                        onClick={handleSendResultsPurl}
                        disabled={resendingResults}
                      >
                        {t("assessment.resendResultsLink")}
                      </Button>
                    </Flex.Item>
                  )}
                </Flex>
              </View>
            </Flex.Item>
            <Flex.Item
              size="65%"
              overflowY="auto"
              height="100%"
              padding="0 small small medium"
            >
              {Object.keys(selectedCycle).length === 0 ? (
                <Alert>{t("assessment.selectCycle")}</Alert>
              ) : loadingPage ? (
                <PlanLoadingSkeleton />
              ) : (
                <Flex wrap="wrap" gap="medium">
                  {includeMission && (
                    <Flex.Item size="100%">
                      <CustomRTE
                        id="mission-desc"
                        label={
                          <Heading level="h2">
                            {t("assessment.mission")}
                          </Heading>
                        }
                        defaultValue={mission}
                        onChange={(newValue) => setMission(newValue)}
                      />
                    </Flex.Item>
                  )}
                  {includeGoalsObjs && (
                    <Flex.Item size="100%" margin="xx-small 0 0 0">
                      <ToggleDetails
                        summary={
                          <Heading level="h2">
                            {t("dashboard.goalsAndObjectives")}
                          </Heading>
                        }
                        defaultExpanded
                      >
                        <List isUnstyled>
                          {selectedCycle.goals.map((goal) => (
                            <List.Item key={goal.id}>
                              <Text weight="bold">{goal.description}</Text>
                              <List>
                                {goal.objectives.map((objective) => (
                                  <List.Item key={objective.id}>
                                    {objective.description}
                                  </List.Item>
                                ))}
                              </List>
                            </List.Item>
                          ))}
                        </List>
                      </ToggleDetails>
                    </Flex.Item>
                  )}
                  <Flex.Item size="100%">
                    <Heading level="h2">
                      {t("assessment.sections.sections")}:
                    </Heading>
                    {sections.map((section, key) => (
                      <ApDataContext.Provider
                        key={`section-${section.id ?? section.index}`}
                        value={{
                          sectionIndex: key,
                          selectedCycle,
                          handleCreateMeasure,
                          handleDeleteMeasure,
                          setSections,
                          extraData: location.state,
                        }}
                      >
                        {location.state.plan?.from_template ? (
                          <TemplateSectionCard
                            key={`section-${key}`}
                            section={section}
                          />
                        ) : (
                          <PlanSectionCard
                            key={`section-${key}`}
                            section={section}
                            lastSection={key === sections.length - 1}
                          />
                        )}
                      </ApDataContext.Provider>
                    ))}
                  </Flex.Item>
                  {!location.state.plan?.from_template && (
                    <Flex.Item size="100%">
                      <Button
                        renderIcon={IconAddLine}
                        display="block"
                        color="primary"
                        onClick={handleAddSection}
                      >
                        {t("assessment.sections.addSection")}
                      </Button>
                    </Flex.Item>
                  )}
                </Flex>
              )}
            </Flex.Item>
          </Flex>
        </Flex.Item>
        <Flex.Item padding="small">
          <View as="div" borderWidth="small 0 0 0">
            <Flex margin="small 0 0 0" gap="small" wrap="wrap">
              <Flex.Item shouldGrow>
                <Button
                  renderIcon={IconArrowStartLine}
                  onClick={() => {
                    if (sections.length > 0) {
                      setConfirmBackDialog(true);
                    } else {
                      navigate("/plan");
                    }
                  }}
                >
                  {t("global.goBack")}
                </Button>
              </Flex.Item>
              <Flex.Item>
                <PreviewResultsButton
                  selectedCycle={selectedCycle}
                  sections={sections}
                  includeMission={includeMission}
                  includeGoalsObjs={includeGoalsObjs}
                  mission={mission}
                  sectionVerification={sectionVerification}
                />
              </Flex.Item>
              <Flex.Item>
                <Button
                  color="primary"
                  onClick={inPlan ? handleUpdatePlan : handleCreatePlan}
                  interaction={
                    sections.length > 0 && !upsertingPlan
                      ? "enabled"
                      : "disabled"
                  }
                >
                  {upsertingPlan ? (
                    <Spinner
                      renderTitle={t("global.pleaseWait")}
                      size="x-small"
                    />
                  ) : inPlan ? (
                    t("assessment.updatePlan")
                  ) : (
                    t("assessment.createPlan")
                  )}
                </Button>
              </Flex.Item>
            </Flex>
          </View>
        </Flex.Item>
      </Flex>
      <DialogYesNo
        message={
          inPlan
            ? t("assessment.goBackInPlanConfirm")
            : t("assessment.goBackConfirm")
        }
        openStatus={confirmBackDialog}
        onClickNo={() => setConfirmBackDialog(false)}
        onClickYes={() => {
          setConfirmBackDialog(false);
          navigate("/plan");
        }}
      />
    </div>
  );
};

export default ManagePlan;

const PlanLoadingSkeleton = () => {
  const [height, setHeight] = useState(window.innerHeight);

  const numSkeletons = Math.floor(height / 230);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Flex wrap="wrap" gap="small">
      {Array.from({ length: numSkeletons }, (_, i) => (
        <Flex.Item size="100%" key={i}>
          <Skeleton
            height={40}
            width="100%"
            style={{ maxWidth: 200, marginBottom: 15 }}
          />
          <Skeleton height={150} width="100%" style={{ marginBottom: 25 }} />
        </Flex.Item>
      ))}
    </Flex>
  );
};

const PreviewResultsButton = ({
  selectedCycle,
  sections,
  includeMission,
  includeGoalsObjs,
  mission,
  sectionVerification,
}) => {
  const [openPreview, setOpenPreview] = useState(false);

  return (
    <>
      <Button
        onClick={() => {
          if (sectionVerification()) setOpenPreview(true);
        }}
        interaction={sections.length > 0 ? "enabled" : "disabled"}
      >
        {t("results.previewResults")}
      </Button>
      <ResultsPreview
        open={openPreview}
        setOpen={setOpenPreview}
        sections={sections}
        data={{
          mission: includeMission ? mission : null,
          goalsObjs: includeGoalsObjs
            ? Object.keys(selectedCycle).length > 0 && selectedCycle
            : null,
        }}
      />
    </>
  );
};
